import React, { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Typography,
  Alert,
  LinearProgress,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { MdCloudUpload, MdOutlineFileDownload } from 'react-icons/md';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMain } from '../../hooks/useMain';

const norm = (s) => (s || '').toString().trim().toLowerCase();

const TEMPLATE_HEADERS = [
  'serviceName',
  'category',
  'price',
  'duration',
  'serviceFor',
  'taxStatus',
  'reminderDays',
];

const getField = (row, key) => {
  if (row[key] !== undefined) return row[key];
  const rowKeys = Object.keys(row);
  const match = rowKeys.find((k) => norm(k) === norm(key));
  return match !== undefined ? row[match] : undefined;
};

// duplicate WITHIN the file = same service name + category + gender
const duplicateKey = (r) => `${norm(r.serviceName)}|${norm(r.category)}|${norm(r.serviceFor)}`;

/**
 * existingServices: pass in the redux `services` array so we can flag rows
 * that already exist for this branch (matched by name + category + gender).
 */
const BulkUploadServiceDialog = ({ onUploaded, existingServices = [] }) => {
  const { activeBranchId, createManyService, setShowMessage } = useMain();

  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const fileInputRef = useRef(null);

  const existingKeySet = useMemo(
    () =>
      new Set(
        existingServices.map(
          (s) => `${norm(s.serviceName)}|${norm(s.category)}|${norm(s.serviceFor)}`
        )
      ),
    [existingServices]
  );

  const resetState = () => {
    setFileName('');
    setParsedRows([]);
    setParseError('');
    setSelectedRows([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetState();
  };

  const downloadExcelTemplate = () => {
    const sampleData = [
      {
        serviceName: 'Hair Cut',
        category: 'Hair',
        price: 500,
        duration: 30,
        serviceFor: 'Male',
        taxStatus: 'Inclusive',
        reminderDays: '10',
      },
      {
        serviceName: 'Skin Treatment',
        category: 'Skin',
        price: 2000,
        duration: 60,
        serviceFor: 'Female',
        taxStatus: 'Exclusive',
        reminderDays: '35',
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sampleData, { header: TEMPLATE_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Services');
    XLSX.writeFile(wb, 'service_template.xlsx');
  };

  const buildFlags = (rows) => {
    const counts = new Map();
    rows.forEach((r) => {
      if (r.invalid) return;
      const key = duplicateKey(r);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return rows.map((r) => ({
      ...r,
      isDuplicateInFile: !r.invalid && counts.get(duplicateKey(r)) > 1,
      alreadyExists: !r.invalid && existingKeySet.has(duplicateKey(r)),
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setSelectedRows([]);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (!jsonData.length) {
          setParseError('The file has no data rows.');
          setParsedRows([]);
          return;
        }

        let mapped = jsonData.map((item, idx) => {
          const serviceName = (getField(item, 'serviceName') || '').toString().trim();
          const category = (getField(item, 'category') || '').toString().trim();
          const priceRaw = getField(item, 'price');
          const price = priceRaw === '' ? '' : Number(priceRaw);
          const durationRaw = getField(item, 'duration');
          const duration = durationRaw === '' ? '' : Number(durationRaw);
          const serviceFor = (getField(item, 'serviceFor') || '').toString().trim();
          const taxStatusRaw = (getField(item, 'taxStatus') || '').toString().trim();
          const taxStatus =
            taxStatusRaw === 'Exclusive' ? 'withoutTax' : taxStatusRaw === 'Inclusive' ? 'withTax' : 'noGst';
          const reminderDays = (getField(item, 'reminderDays') || '').toString().trim();

          const row = {
            rowIndex: idx + 2,
            serviceName,
            category,
            price: isNaN(price) ? '' : price,
            duration: isNaN(duration) ? '' : duration,
            serviceFor,
            taxStatus,
            reminderDays,
          };

          const missingFields = [];
          if (!row.serviceName) missingFields.push('serviceName');
          if (row.price === '') missingFields.push('price');
          if (!row.serviceFor) missingFields.push('serviceFor');

          row.invalid = missingFields.length > 0;
          row.invalidReason = missingFields.length ? `Missing/invalid: ${missingFields.join(', ')}` : '';
          return row;
        });

        mapped = buildFlags(mapped);
        setParsedRows(mapped);

        // select all valid rows EXCEPT already-existing ones by default
        setSelectedRows(
          mapped.filter((r) => !r.invalid && !r.alreadyExists).map((r) => r.rowIndex)
        );
      } catch (err) {
        setParseError('Could not read this file. Please upload a valid .xlsx or .xls file.');
        setParsedRows([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRemoveRow = (rowIndex) => {
    setParsedRows((prev) => buildFlags(prev.filter((r) => r.rowIndex !== rowIndex)));
    setSelectedRows((prev) => prev.filter((i) => i !== rowIndex));
  };

  const handleRemoveAllFileDuplicates = () => {
    const seen = new Set();
    const deduped = [];
    parsedRows.forEach((r) => {
      if (r.invalid) {
        deduped.push(r);
        return;
      }
      const key = duplicateKey(r);
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(r);
    });
    const removedCount = parsedRows.length - deduped.length;
    setParsedRows(buildFlags(deduped));
    setSelectedRows((prev) => prev.filter((i) => deduped.some((r) => r.rowIndex === i)));
    setShowMessage?.({
      message: removedCount > 0 ? `Removed ${removedCount} duplicate row(s)` : 'No duplicates found in file',
      messageType: removedCount > 0 ? 'success' : 'error',
    });
  };

  const selectableRows = parsedRows.filter((r) => !r.invalid);
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedRows(allSelected ? [] : selectableRows.map((r) => r.rowIndex));
  };

  const toggleSelectRow = (rowIndex) => {
    setSelectedRows((prev) =>
      prev.includes(rowIndex) ? prev.filter((i) => i !== rowIndex) : [...prev, rowIndex]
    );
  };

  const invalidCount = parsedRows.filter((r) => r.invalid).length;
  const fileDuplicateCount = parsedRows.filter((r) => r.isDuplicateInFile).length;
  const alreadyExistsCount = parsedRows.filter((r) => r.alreadyExists).length;
  const readyCount = selectableRows.length;

  const handleSubmit = async () => {
    if (parsedRows.length === 0) {
      setShowMessage({ message: 'Upload a valid file', messageType: 'error' });
      return;
    }
    if (selectedRows.length === 0) {
      setShowMessage({ message: 'Select at least one row to upload', messageType: 'error' });
      return;
    }

    const rowsToUpload = parsedRows
      .filter((r) => selectedRows.includes(r.rowIndex))
      .map((r) => ({
        serviceName: r.serviceName,
        category: r.category,
        price: r.price,
        duration: r.duration,
        serviceFor: r.serviceFor,
        taxStatus: r.taxStatus,
        reminderDays: r.reminderDays,
      }));

    setSubmitting(true);
    try {
      const res = await createManyService(activeBranchId, { bulkData: rowsToUpload });
      if (res?.statusCode === 200) {
        setShowMessage({ message: res.message || 'Uploaded successfully', messageType: 'success' });
        handleClose();
        onUploaded?.();
      } else {
        setShowMessage({ message: res?.message || 'Upload failed', messageType: 'error' });
      }
    } catch (err) {
      setShowMessage({ message: err.message || 'Something went wrong', messageType: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<MdCloudUpload />}
        sx={{ background: '#6174DD', height: '40px', minWidth: '140px' }}
        onClick={handleOpen}
      >
        Services Upload
      </Button>

      <Dialog
        open={open}
        onClose={submitting ? undefined : handleClose}
        maxWidth={parsedRows.length > 0 ? 'lg' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          Bulk Upload Services{' '}
          <Tooltip
            title="Upload an excel file with columns: serviceName, category, price, duration, serviceFor, taxStatus, reminderDays. Rows matching an existing service (same name + category + gender) are flagged — uploading them will UPDATE that service's existing details. Uncheck any row you don't want touched."
            placement="right"
            arrow
          >
            <IconButton size="small" sx={{ p: 0 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" component="label" disabled={submitting}>
              Choose File
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileSelect} />
            </Button>
            <Button
              variant="outlined"
              startIcon={<MdOutlineFileDownload />}
              onClick={downloadExcelTemplate}
              disabled={submitting}
            >
              Download Sample File
            </Button>
            {fileName && <Typography variant="body2">{fileName}</Typography>}
          </Box>

          {submitting && <LinearProgress sx={{ mb: 2 }} />}

          {parseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
          )}

          {alreadyExistsCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {alreadyExistsCount} service(s) in this file already exist (matched by name + category + gender). They
              are unchecked by default — if you leave them checked and upload, their existing details will be
              <strong> updated/overwritten</strong> with the values from this file. Uncheck any you don't want to touch.
            </Alert>
          )}

          {parsedRows.length > 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                <Chip label={`${parsedRows.length} rows total`} />
                <Chip label={`${readyCount} valid`} color="success" />
                {invalidCount > 0 && <Chip label={`${invalidCount} invalid`} color="error" />}
                {fileDuplicateCount > 0 && <Chip label={`${fileDuplicateCount} duplicate rows in file`} color="warning" />}
                {alreadyExistsCount > 0 && <Chip label={`${alreadyExistsCount} already present`} color="info" />}
                <Chip label={`${selectedRows.length} selected`} color="primary" variant="outlined" />
                {fileDuplicateCount > 0 && (
                  <Button size="small" color="warning" variant="outlined" onClick={handleRemoveAllFileDuplicates}>
                    Remove file duplicates (keep first)
                  </Button>
                )}
              </Box>

              <Box sx={{ maxHeight: '440px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleSelectAll}
                          disabled={selectableRows.length === 0}
                        />
                      </TableCell>
                      <TableCell>Row</TableCell>
                      <TableCell>Service Name</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>For</TableCell>
                      <TableCell>Tax Status</TableCell>
                      <TableCell>Reminder Days</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Remove</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedRows.map((r) => (
                      <TableRow
                        key={r.rowIndex}
                        selected={selectedRows.includes(r.rowIndex)}
                        sx={{
                          backgroundColor: r.invalid
                            ? '#ffebee'
                            : r.alreadyExists
                            ? '#e3f2fd'
                            : r.isDuplicateInFile
                            ? '#fff3e0'
                            : 'inherit',
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.includes(r.rowIndex)}
                            onChange={() => toggleSelectRow(r.rowIndex)}
                            disabled={r.invalid}
                          />
                        </TableCell>
                        <TableCell>{r.rowIndex}</TableCell>
                        <TableCell>{r.serviceName || '—'}</TableCell>
                        <TableCell>{r.category || '—'}</TableCell>
                        <TableCell>{r.price === '' ? '—' : r.price}</TableCell>
                        <TableCell>{r.duration === '' ? '—' : r.duration}</TableCell>
                        <TableCell>{r.serviceFor || '—'}</TableCell>
                        <TableCell>{r.taxStatus}</TableCell>
                        <TableCell>{r.reminderDays || '—'}</TableCell>
                        <TableCell>
                          {r.invalid ? (
                            <Tooltip title={r.invalidReason || 'Invalid row'}>
                              <Chip size="small" label="Invalid" color="error" />
                            </Tooltip>
                          ) : r.alreadyExists ? (
                            <Chip size="small" label="Already exists" color="info" />
                          ) : r.isDuplicateInFile ? (
                            <Chip size="small" label="Duplicate in file" color="warning" />
                          ) : (
                            <Chip size="small" label="New" color="success" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color={r.invalid ? 'error' : 'text.secondary'}>
                            {r.invalid
                              ? r.invalidReason
                              : r.alreadyExists
                              ? 'Matches an existing service — will update if checked'
                              : r.isDuplicateInFile
                              ? 'Same name+category+gender as another row'
                              : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove row">
                            <IconButton size="small" onClick={() => handleRemoveRow(r.rowIndex)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ background: '#6174DD' }}
            onClick={handleSubmit}
            disabled={submitting || selectedRows.length === 0 || !!parseError}
          >
            {submitting ? 'Uploading...' : `Upload ${selectedRows.length || ''} Selected Service(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadServiceDialog;